"use client";

/**
 * ErpTopbar — 56px sticky topbar (1:1 mockup port).
 *
 * Mockup: docs/mockups/erp-skeleton-v1-popunjen.html (lin 884-1055).
 * Layout (PRAVILO 13 — SVETA ZONA desno se NE pomjera):
 *
 *   [G logo + "Grozd"] [Operacije ▼] [Podešavanja]   [Search][Moduli][Bell] | [Tenant] [Avatar]
 *      lijevi brand     mega meni        item              SVETA ZONA (flex-shrink-0)
 *
 * Pravila:
 *  - PRAVILO 11: meni na klik (Mega meni i User meni hendlaju)
 *  - PRAVILO 12: CSS varijable, dark mode, Manrope/Inter, crnogorski latinica
 *  - PRAVILO 13: desna sveta zona — flex-shrink-0, NIKAD pomjera
 */

import * as React from "react";
import { GrozdLogo } from "@grozd/ui";
import type { AuthUser } from "@grozd/auth-client";
import {
  Menu,
  ChevronDown,
  Settings,
  Search,
  LayoutGrid,
  Bell,
} from "@grozd/icons";
import { ErpUserMenu } from "./ErpUserMenu";
import { ErpMegaMenu } from "./ErpMegaMenu";

interface ErpTopbarProps {
  user?: AuthUser | null;
  /** Tenant ime, default "DOT PRO d.o.o." */
  tenantName?: string;
  /** Tenant inicijali, default "DP" */
  tenantInitials?: string;
}

export function ErpTopbar({
  user,
  tenantName = "DOT PRO d.o.o.",
  tenantInitials = "DP",
}: ErpTopbarProps) {
  const [megaOpen, setMegaOpen] = React.useState(false);
  const megaTriggerRef = React.useRef<HTMLButtonElement>(null);

  const handleMegaToggle = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMegaOpen((p) => !p);
  }, []);

  return (
    <header
      className="sticky top-0 z-30 flex items-center"
      style={{
        height: 56,
        background: "var(--surface-card)",
        borderBottom: "1px solid var(--n100)",
        padding: "0 16px",
        gap: 8,
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      {/* BRAND — lijevo (240px - border) */}
      <div
        className="flex items-center gap-2.5"
        style={{
          width: 240 - 16,
          flexShrink: 0,
          paddingRight: 16,
          borderRight: "1px solid var(--n100)",
          height: "100%",
        }}
      >
        <span
          className="flex items-center justify-center"
          style={{
            width: 30,
            height: 30,
            borderRadius: 10,
            background: "linear-gradient(135deg, var(--primary), var(--deep))",
            color: "#fff",
            flexShrink: 0,
            boxShadow: "0 2px 8px rgba(90,58,181,0.25)",
          }}
        >
          <GrozdLogo size={18} color="#fff" />
        </span>
        <span
          className="font-brand"
          style={{
            fontWeight: 800,
            fontSize: 15.5,
            color: "var(--ink)",
            letterSpacing: "-0.02em",
          }}
        >
          Grozd
        </span>
      </div>

      {/* CENTER NAV — Operacije mega + Podešavanja */}
      <nav
        className="flex items-center gap-1"
        aria-label="Glavna navigacija"
        style={{ flex: 1, minWidth: 0, overflow: "hidden" }}
      >
        <div style={{ position: "relative" }}>
          <button
            ref={megaTriggerRef}
            type="button"
            onClick={handleMegaToggle}
            className="erp-tb__item"
            data-active={megaOpen ? "true" : undefined}
            aria-haspopup="menu"
            aria-expanded={megaOpen}
            style={{
              height: 36,
              padding: "0 12px",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13.5,
              fontWeight: megaOpen ? 600 : 500,
              color: megaOpen ? "var(--primary)" : "var(--n600)",
              background: megaOpen ? "var(--light)" : "transparent",
              border: "none",
              borderRadius: 10,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 120ms",
            }}
          >
            <Menu size={14} strokeWidth={2} />
            Operacije
            <ChevronDown
              size={14}
              strokeWidth={2}
              style={{
                transform: megaOpen ? "rotate(180deg)" : undefined,
                transition: "transform 200ms",
              }}
            />
          </button>
          <ErpMegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} />
        </div>

        <a
          href="/podesavanja"
          className="erp-tb__item"
          style={{
            height: 36,
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13.5,
            fontWeight: 500,
            color: "var(--n600)",
            borderRadius: 10,
            textDecoration: "none",
            whiteSpace: "nowrap",
            transition: "all 120ms",
          }}
        >
          <Settings size={14} strokeWidth={2} />
          Podešavanja
        </a>
      </nav>

      {/* SVETA ZONA — desno (PRAVILO 13: nikad se ne pomjera) */}
      <div
        className="flex items-center gap-1"
        style={{ flexShrink: 0 }}
        data-testid="erp-topbar-sacred"
      >
        <button
          type="button"
          className="erp-tb__icon"
          aria-label="Pretraga (Ctrl+K)"
          title="Pretraga (Ctrl+K)"
          style={iconBtnStyle}
        >
          <Search size={18} strokeWidth={1.8} />
        </button>
        <button
          type="button"
          className="erp-tb__icon"
          aria-label="Moduli"
          title="Moduli"
          style={iconBtnStyle}
        >
          <LayoutGrid size={18} strokeWidth={1.8} />
        </button>
        <button
          type="button"
          className="erp-tb__icon"
          aria-label="Notifikacije (3 nove)"
          title="Notifikacije (3 nove)"
          style={{ ...iconBtnStyle, position: "relative" }}
        >
          <Bell size={18} strokeWidth={1.8} />
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 999,
              background: "var(--danger)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid var(--surface-card)",
            }}
          >
            3
          </span>
        </button>

        <span
          aria-hidden
          style={{
            width: 1,
            height: 24,
            background: "var(--n100)",
            margin: "0 6px",
            flexShrink: 0,
          }}
        />

        <button
          type="button"
          className="erp-tb__tenant"
          aria-label="Promijeni tenant"
          title="Promijeni tenant"
          style={{
            height: 36,
            padding: "0 10px 0 8px",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            transition: "all 120ms",
            maxWidth: 200,
            border: "1px solid transparent",
            background: "transparent",
          }}
        >
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: "linear-gradient(135deg,#FBBF24,#F97316)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 11,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {tenantInitials}
          </span>
          <span
            className="font-body"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {tenantName}
          </span>
          <ChevronDown size={14} strokeWidth={2} style={{ color: "var(--n400)", flexShrink: 0 }} />
        </button>

        <ErpUserMenu user={user} />
      </div>

      <style jsx>{`
        :global(.erp-tb__item):hover {
          background: var(--tint);
          color: var(--primary);
        }
        :global(.erp-tb__icon):hover {
          background: var(--tint) !important;
          color: var(--primary) !important;
        }
        :global(.erp-tb__tenant):hover {
          background: var(--tint) !important;
          border-color: var(--n100) !important;
        }
      `}</style>
    </header>
  );
}

const iconBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--n600)",
  cursor: "pointer",
  transition: "all 120ms",
  background: "transparent",
  border: "none",
};

export default ErpTopbar;
